/**
 * "What needs me" — the decision queue on Home.
 *
 * A campaign has three moments that genuinely require a human: approve the
 * creators, approve the content, launch. Agents run everything between those
 * gates; this queue puts the gates themselves in front of the user, with a
 * one-click path to the place where the decision is made. Without it, the
 * user has to tour campaign pages to discover that 20 creators have been
 * waiting three days for a yes.
 *
 * Renders nothing while loading and nothing when the queue is empty — an
 * empty decision queue is the good state and doesn't need to announce itself.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { UserCheck, FileCheck, Rocket, Bot, ChevronRight } from 'lucide-react';
import { coworkApi, type NeedsMeItem } from '@/cowork/omniad/api/coworkApi';
import { isCoworkLive } from '@/cowork/live/coworkClient';

const KIND_META: Record<NeedsMeItem['kind'], { icon: typeof UserCheck; tint: string; bg: string }> = {
  curation: { icon: UserCheck, tint: 'text-[#be43ff]', bg: 'bg-[#be43ff]/10' },
  content: { icon: FileCheck, tint: 'text-[#0ba5ec]', bg: 'bg-[#0ba5ec]/10' },
  agent_approval: { icon: Bot, tint: 'text-[#f79009]', bg: 'bg-[#f79009]/10' },
  launch: { icon: Rocket, tint: 'text-[#12b76a]', bg: 'bg-[#12b76a]/10' },
};

function hrefFor(item: NeedsMeItem): string {
  switch (item.kind) {
    case 'curation':
      // Creator decisions live on the campaign's profiles tab.
      return `/campaigns/${item.campaignId}/profiles`;
    case 'content':
      return `/campaigns/${item.campaignId}/content-approval`;
    case 'launch':
      return `/campaigns/${item.campaignId}`;
    case 'agent_approval':
      // Agent approvals (wiki edits, Meta shells…) are decided on the Approvals page.
      return '/agents/approvals';
  }
}

export function NeedsMeQueue() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['cowork', 'needs-me'],
    queryFn: () => coworkApi.needsMe.list(),
    enabled: isCoworkLive(),
    refetchInterval: 90_000,
    staleTime: 60_000,
  });

  const items = data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <section aria-label="Decisions waiting on you">
      <div className="mb-2.5 flex items-baseline gap-2">
        <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>
          What needs you
        </h2>
        <span className="text-xs text-[#98a2b3]">
          {items.length} decision{items.length === 1 ? '' : 's'} waiting — everything else is running
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
        {items.slice(0, 6).map((item, i) => {
          const meta = KIND_META[item.kind];
          const Icon = meta.icon;
          return (
            <button
              key={`${item.kind}-${item.campaignId ?? item.approvalId ?? i}`}
              type="button"
              onClick={() => navigate(hrefFor(item))}
              className="group flex items-center gap-3 rounded-xl border border-gray-200/80 bg-white px-3.5 py-3 text-left shadow-sm transition-all hover:border-[#be43ff]/30 hover:shadow-md"
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
                <Icon className={`h-4.5 w-4.5 ${meta.tint}`} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
                  {item.title}
                </span>
                <span className="block truncate text-xs text-[#667085]">{item.detail}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#be43ff]" aria-hidden />
            </button>
          );
        })}
      </div>
    </section>
  );
}
